import { Button, Checkbox, TextCaption } from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useMemo, useState } from 'react';

import { ConfigPanel } from '../../components/table/src/config-panel';
import { useProStudioViewerRole } from '../../hooks';
import { tableStore, type TableNode } from '../../stores/table-store';
import { isGroupRow } from '../../stores/utils/group-row-guards';
import {
  suggestAgents,
  type SuggestedAgent,
} from '../../stores/utils/suggest-agents';
import { useTopUnderBuildBar } from '../build-panel-position';
import { nameSuggestedAgents } from './suggest-agents-api';

import './suggest-agents-panel.scss';

interface Draft {
  agent: SuggestedAgent;
  name: string;
  include: boolean;
  /** Distinct node names, for the row's sample line and for AI naming. */
  nodeNames: string[];
}

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** Rows in no group: what the suggestion is for. */
const ungroupedRows = (rows: TableNode[]) =>
  rows.filter(
    (r) => !isGroupRow(r) && !r.settings?.groupId && r.hidden !== true
  );

/**
 * An agent's distinct node names, looked up in `byId` — the panel's own index
 * of the rows it grouped. Not `tableStore.getRowById`, which scans every row:
 * thousands of lookups on a big solution took seconds.
 */
const nodeNamesOf = (agent: SuggestedAgent, byId: Map<string, TableNode>) => {
  const names = agent.rowIds
    .map((id) => String(byId.get(id)?.nodeName ?? '').trim())
    .filter(Boolean);
  return [...new Set(names)];
};

/**
 * "Suggest agents" — groups for a solution built without any, found by
 * `suggestAgents` from its node names, numbers and routing. The designer
 * reviews them here (keep or drop each, rename it, or have AI name the ones
 * the grouping could only guess), and nothing is written until Create.
 */
export const SuggestAgentsPanel: FC = observer(() => {
  const isViewer = useProStudioViewerRole();
  const top = useTopUnderBuildBar();
  const rows = ungroupedRows(tableStore.getAllSortedRows());
  const signature = rows.map((r) => r.id).join(',');

  const suggestion = useMemo(
    () => suggestAgents(rows),
    // Recompute only when which rows are ungrouped changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature]
  );

  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [naming, setNaming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    const byId = new Map(rows.map((r) => [r.id, r]));
    setDrafts(
      suggestion.agents.map((agent) => ({
        agent,
        name: agent.name,
        include: true,
        nodeNames: nodeNamesOf(agent, byId),
      }))
    );
    setProblem(null);
    // `rows` is read for names only; `suggestion` already changes with them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion]);

  const update = (key: string, change: Partial<Draft>) =>
    setDrafts((ds) =>
      ds.map((d) => (d.agent.key === key ? { ...d, ...change } : d))
    );

  const chosen = drafts.filter((d) => d.include);
  const chosenNodes = chosen.reduce((n, d) => n + d.agent.rowIds.length, 0);
  const guessed = drafts.filter(
    (d) => d.include && d.agent.needsName && d.name === d.agent.name
  );

  const nameWithAi = async () => {
    setNaming(true);
    setProblem(null);
    try {
      const names = await nameSuggestedAgents(
        guessed.map((d) => ({
          key: d.agent.key,
          current: d.name,
          nodeNames: d.nodeNames.slice(0, 15),
        }))
      );
      setDrafts((ds) =>
        ds.map((d) =>
          names[d.agent.key] ? { ...d, name: names[d.agent.key] } : d
        )
      );
    } catch (err) {
      setProblem(
        `Couldn’t name them: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setNaming(false);
    }
  };

  const create = async () => {
    setCreating(true);
    setProblem(null);
    // Grouping thousands of rows holds the page for a while on a big
    // solution; let "Creating…" paint first, so it doesn't look frozen.
    await new Promise((resolve) =>
      window.requestAnimationFrame(() => window.setTimeout(resolve, 0))
    );
    const made = tableStore.applySuggestedAgents(
      chosen.map((d) => ({ name: d.name, rowIds: d.agent.rowIds }))
    );
    setCreating(false);
    if (made) tableStore.setSuggestAgentsPanelOpen(false);
    else setProblem('None of these could be created — the nodes have changed.');
  };

  const close = () => tableStore.setSuggestAgentsPanelOpen(false);

  return (
    <div
      className="c-build-actions c-suggest-agents"
      style={{ top }}
      data-testid="bobcat-suggest-agents-panel"
    >
      <ConfigPanel title="Suggest agents" onClose={close}>
        <TextCaption variant="secondary">
          Groups for the {plural(rows.length, 'node')} not in an agent yet,
          Nothing changes until you create them.
        </TextCaption>

        {drafts.length === 0 ? (
          <p className="c-suggest-agents__empty">
            No groups stand out — the names, numbers and routes don’t split
            these nodes into agents. Group them by hand from the table.
          </p>
        ) : (
          <>
            <div className="c-suggest-agents__bar">
              <Checkbox
                size="small"
                checked={chosen.length === drafts.length}
                indeterminate={
                  chosen.length > 0 && chosen.length < drafts.length
                }
                onChange={(e) => {
                  const on = e.target.checked;
                  setDrafts((ds) => ds.map((d) => ({ ...d, include: on })));
                }}
              >
                {plural(chosen.length, 'agent')} · {plural(chosenNodes, 'node')}
              </Checkbox>
              {guessed.length > 0 && !isViewer && (
                <Button
                  size="small"
                  variant="secondary"
                  disabled={naming}
                  test-id="bobcat-suggest-agents-name-ai"
                  onClick={nameWithAi}
                >
                  {naming ? 'Naming…' : `Name ${guessed.length} with AI`}
                </Button>
              )}
            </div>

            <ul className="c-suggest-agents__list">
              {drafts.map((d) => (
                <li
                  key={d.agent.key}
                  className={`c-suggest-agents__item${
                    d.include ? '' : ' c-suggest-agents__item--off'
                  }`}
                  data-testid="bobcat-suggest-agents-item"
                >
                  <Checkbox
                    size="small"
                    checked={d.include}
                    aria-label={`Include ${d.name}`}
                    onChange={(e) =>
                      update(d.agent.key, {
                        include: e.target.checked,
                      })
                    }
                  />
                  <div className="c-suggest-agents__body">
                    <div className="c-suggest-agents__head">
                      <input
                        className="c-suggest-agents__name"
                        value={d.name}
                        aria-label="Agent name"
                        disabled={isViewer}
                        onChange={(e) =>
                          update(d.agent.key, { name: e.target.value })
                        }
                      />
                      <span className="c-suggest-agents__count">
                        {plural(d.agent.rowIds.length, 'node')}
                      </span>
                    </div>
                    <span className="c-suggest-agents__sample">
                      {d.agent.needsName && d.name === d.agent.name && (
                        <span className="c-suggest-agents__guess">
                          {d.agent.nameSource === 'menu'
                            ? 'Named from its menu button · '
                            : 'Guessed name · '}
                        </span>
                      )}
                      {d.nodeNames.slice(0, 3).join(', ')}
                      {d.nodeNames.length > 3 ? '…' : ''}
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            {(suggestion.leftover.length > 0 ||
              suggestion.shared.length > 0) && (
              <TextCaption variant="secondary">
                Left ungrouped:{' '}
                {[
                  suggestion.shared.length &&
                    `${plural(suggestion.shared.length, 'shared node')} every agent uses (error handling, escalation, the end)`,
                  suggestion.leftover.length &&
                    `${plural(suggestion.leftover.length, 'node')} nothing placed`,
                ]
                  .filter(Boolean)
                  .join('; ')}
                .
              </TextCaption>
            )}

            {problem && (
              <p className="c-suggest-agents__problem" role="alert">
                {problem}
              </p>
            )}

            {!isViewer && (
              <div className="c-suggest-agents__footer">
                <Button
                  size="small"
                  variant="primary"
                  disabled={!chosen.length || creating}
                  test-id="bobcat-suggest-agents-create"
                  onClick={create}
                >
                  {creating
                    ? `Creating ${plural(chosen.length, 'agent')}…`
                    : `Create ${plural(chosen.length, 'agent')}`}
                </Button>
              </div>
            )}
          </>
        )}
      </ConfigPanel>
    </div>
  );
});
